// hooks/usePermissions.js
import { useAuth } from './useAuth';

export const usePermissions = () => {
  const { user } = useAuth();
  
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  const permissions = {
    // View Permissions
    canViewDashboard: isAdmin,
    canViewUsers: isAdmin,
    canViewAdmins: isAdmin,
    canViewHospitals: isAdmin,
    canViewPharmacies: isAdmin,
    canViewAmbulances: isAdmin,
    canViewPayments: isAdmin,
    canViewReports: isAdmin,
    canViewAnalytics: isAdmin,
    canViewContent: isAdmin,
    canViewSettings: isAdmin,
    
    // Approval & Verification Permissions
    canApproveUsers: isAdmin,
    canVerifyUsers: isAdmin,
    canApproveHospitals: isAdmin,
    canApprovePharmacies: isAdmin,
    canApproveAmbulances: isAdmin,
    canApprovePayments: isAdmin,
    canVerifyDocuments: isAdmin,
    
    // Report Permissions
    canGenerateReports: isAdmin,
    canExportReports: isAdmin,
    canViewFinancialReports: isAdmin,
    canViewUserReports: isAdmin,
    
    // Edit Permissions (Admin Level)
    canEditHospitals: isAdmin,
    canEditPharmacies: isAdmin,
    canEditAmbulances: isAdmin,
    canEditContent: isAdmin,
    canToggleUserStatus: isAdmin,
    canToggleHospitalStatus: isAdmin,
    canTogglePharmacyStatus: isAdmin,
    canToggleAmbulanceStatus: isAdmin,
    
    // Super Admin Edit Permissions
    canEditUserProfile: isSuperAdmin,
    canEditUserEmail: isSuperAdmin,
    canEditUserPhone: isSuperAdmin,
    canEditUserRole: isSuperAdmin,
    canResetUserPassword: isSuperAdmin,
    
    // Delete Permissions (Super Admin Only)
    canDeleteUsers: isSuperAdmin,
    canDeleteHospitals: isSuperAdmin,
    canDeletePharmacies: isSuperAdmin,
    canDeleteAmbulances: isSuperAdmin,
    canDeleteContent: isSuperAdmin,
    canDeletePayments: isSuperAdmin,
    
    // Admin Management (Super Admin Only)
    canCreateAdmins: isSuperAdmin,
    canEditAdmins: isSuperAdmin,
    canDeleteAdmins: isSuperAdmin,
    canToggleAdminStatus: isSuperAdmin,
    canViewAdminLogs: isSuperAdmin,
    
    // System Management (Super Admin Only)
    canEditSystemSettings: isSuperAdmin,
    canManageRoles: isSuperAdmin,
    canManagePermissions: isSuperAdmin,
    
    // Payment Management (Super Admin Only)
    canRefundPayments: isSuperAdmin,
    canAdjustPayments: isSuperAdmin,
    
    // Role Info
    isSuperAdmin,
    isAdmin,
    role: user?.role || null
  };
  
  const hasPermission = (permission) => {
    return permissions[permission] || false;
  };
  
  const hasAnyPermission = (permissionList) => {
    return permissionList.some(permission => permissions[permission]);
  };
  
  const hasAllPermissions = (permissionList) => {
    return permissionList.every(permission => permissions[permission]);
  };
  
  const getPermissionLevel = () => {
    if (isSuperAdmin) {
      return 'Full System Access - Can view, edit, delete all content and manage admins';
    }
    if (isAdmin) {
      return 'Admin Access - Can view all pages, approve users/payments, but cannot delete or edit user profiles';
    }
    return 'No admin access';
  };
  
  return {
    ...permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getPermissionLevel
  };
};

export default usePermissions;